import { useState } from 'react';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import toast from 'react-hot-toast';
import { z } from 'zod';

import { AnimatedCard } from '@components/patterns';
import {
  Button,
  cn,
  FormField,
  Input,
  Select,
  useDialogContext,
} from '@components/primitives';

import { api } from '@services/apiService';
import { adminTeamResourceUsageLimits } from '@utils/resources/admin';
import time from '@utils/time';

import styles from './ResourceUsageLimitUpdate.module.css';

const useTeamResourceUsageLimitsQuery = (teamUuid: string) =>
  useQuery({
    queryKey: ['teamResourceUsageLimitsQuery', teamUuid],
    queryFn: () =>
      api.get(adminTeamResourceUsageLimits(teamUuid)).json<ResourceUsage>(),
  });

type TeamResourceUsageLimitUpdate = {
  resource: string;
  limit: string;
};

type TeamResourceUsageLimitUpdateForm = Omit<
  TeamResourceUsageLimitUpdate,
  'resource'
>;

const useCreateTeamResourceUsageLimitMutation = (teamUuid: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (resourceUsageLimitsUpdate: TeamResourceUsageLimitUpdate) =>
      api
        .post(adminTeamResourceUsageLimits(teamUuid), {
          json: resourceUsageLimitsUpdate,
        })
        .json(),
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['teamResourceUsageLimitsQuery', teamUuid],
      });
    },
  });
};

type ResourceUsageLimitType =
  | 'relay'
  | 'function'
  | 'dataRole'
  | 'webhook'
  | 'enclave';

type ResourceUsageData = {
  usage: number;
  limit: number;
  isLimitReached: boolean;
  overrideSetAt?: string;
};

type ResourceUsage = Record<ResourceUsageLimitType, ResourceUsageData>;

const resourceUsageLimitUpdateSchema = z.object({
  limit: z.number().int().min(0),
});

type ResourceUsageLimitTabProps = {
  resource: ResourceUsageLimitType;
  data: ResourceUsageData;
  teamUuid: string;
};

const ResourceUsageLimitTab = ({
  resource,
  data,
  teamUuid,
}: ResourceUsageLimitTabProps) => {
  const dialog = useDialogContext();
  const { mutateAsync: createTeamResourceUsageLimit } =
    useCreateTeamResourceUsageLimitMutation(teamUuid);

  const {
    register,
    handleSubmit,
    formState: { isSubmitting, errors },
  } = useForm<TeamResourceUsageLimitUpdateForm>({
    defaultValues: { limit: data.limit.toString() },
    mode: 'onTouched',
    resolver: zodResolver(resourceUsageLimitUpdateSchema),
  });

  const submissionHandler = async (
    values: TeamResourceUsageLimitUpdateForm
  ) => {
    try {
      await createTeamResourceUsageLimit({ ...values, resource });
      toast.success(`${resource} limit created`);
      dialog.close();
    } catch {
      toast.error('Failed to update team limits');
    }
  };

  if (!resource || !data) return null;

  return (
    <AnimatedCard className={styles.tab}>
      <header>
        <h2>
          {resource} Limits
          <span></span>
        </h2>
        <p className={cn(data.isLimitReached && 'color-red-dark text-bold')}>
          {data.usage}/
          <span
            className={cn(
              data.isLimitReached ? 'color-red-dark' : 'color-primary'
            )}
          >
            {data.limit}
          </span>
        </p>
      </header>
      {data.overrideSetAt && (
        <div className={styles.overrideDetails}>
          <b>Default Limits Overridden for Team</b>
          <p>
            Override created:{' '}
            <code>
              {time(data.overrideSetAt).format('MMMM DD, YYYY, HH:mm')}
            </code>
          </p>
        </div>
      )}
      <form onSubmit={handleSubmit(submissionHandler)}>
        <FormField
          className="mb-5"
          label={`${resource} limit`}
          error={errors.limit?.message}
        >
          <Input
            {...register('limit', { valueAsNumber: true })}
            placeholder={data.limit.toString()}
            defaultValue={data.limit.toString()}
            error={Boolean(errors.limit)}
          />
        </FormField>
        <Button loading={isSubmitting}>Set team {resource} limits</Button>
      </form>
    </AnimatedCard>
  );
};

type ResourceUsageLimitUpdateModalProps = {
  teamUuid: string;
};

export const ResourceUsageLimitUpdateModal = ({
  teamUuid,
}: ResourceUsageLimitUpdateModalProps) => {
  const { data } = useTeamResourceUsageLimitsQuery(teamUuid);
  const [activeTab, setActiveTab] = useState<ResourceUsageLimitType>('relay');
  if (data)
    return (
      <div className={styles.container}>
        <Select
          onChange={(v) => setActiveTab(v as ResourceUsageLimitType)}
          value={activeTab}
        >
          {Object.keys(data).map((resource) => (
            <Select.Option
              style={{ textTransform: 'capitalize' }}
              key={resource}
              value={resource}
            >
              {resource}
            </Select.Option>
          ))}
        </Select>
        <main className={styles.content}>
          <ResourceUsageLimitTab
            key={activeTab}
            resource={activeTab}
            data={data[activeTab]}
            teamUuid={teamUuid}
          />
        </main>
      </div>
    );
};
